/**
 * Parser/serializer for the `--` prompt-section protocol (docs/prompt-builder-plan.md §4).
 *
 * A marker line `-- <slug>` (optionally `-- <slug> #<n>`) opens a typed section; everything
 * until the next marker line (or end of input) is its body. Text before the first marker is
 * an untyped free block (slug null). Marker lines are `--` comments, so the existing
 * generation paths (filter_prompt_comments / filterPromptComments) strip them unchanged.
 *
 * Losslessness: blocks tile the raw string — concatenating `raw.slice(start, end)` over
 * parseSections(raw) reproduces raw exactly. Mutation helpers rewrite only the target
 * block's range and never reformat text they didn't touch.
 */

export interface PromptSection {
  slug: string | null;   // null = free text before the first marker (untyped block)
  occurrence: number;    // 0-based among sections with the same slug
  text: string;          // body text between this marker line and the next marker (or end); preserved verbatim
  start: number;         // char offset in raw where this block starts (the marker line, or 0 for the free block)
  end: number;           // char offset where the block ends (exclusive)
}

// Whole-line marker: `-- slug` with optional `#n` occurrence hint. A `--` line that does
// not match (e.g. `-- note to self!`) is an ordinary comment and stays in the section text.
// Trailing `\s*` swallows a `\r`, so CRLF input parses without special-casing.
const MARKER_RE = /^--\s*([a-z_][a-z0-9_]*)\s*(?:#\d+)?\s*$/;

interface MarkerLine {
  slug: string;
  start: number;      // offset of the marker line's first char
  bodyStart: number;  // offset just past the marker line's newline (or end of input)
}

function findMarkers(raw: string): MarkerLine[] {
  const markers: MarkerLine[] = [];
  let lineStart = 0;
  while (lineStart <= raw.length) {
    let lineEnd = raw.indexOf('\n', lineStart);
    let bodyStart: number;
    if (lineEnd === -1) {
      lineEnd = raw.length;
      bodyStart = raw.length;
    } else {
      bodyStart = lineEnd + 1;
    }
    const match = MARKER_RE.exec(raw.slice(lineStart, lineEnd));
    if (match) {
      markers.push({ slug: match[1], start: lineStart, bodyStart });
    }
    if (lineEnd === raw.length) break;
    lineStart = lineEnd + 1;
  }
  return markers;
}

export function parseSections(raw: string): PromptSection[] {
  if (raw === '') return [];
  const markers = findMarkers(raw);
  if (markers.length === 0) {
    return [{ slug: null, occurrence: 0, text: raw, start: 0, end: raw.length }];
  }
  const sections: PromptSection[] = [];
  if (markers[0].start > 0) {
    sections.push({
      slug: null,
      occurrence: 0,
      text: raw.slice(0, markers[0].start),
      start: 0,
      end: markers[0].start,
    });
  }
  // Map, not a plain object: slugs like 'constructor' match the marker pattern.
  const counts = new Map<string, number>();
  for (let i = 0; i < markers.length; i++) {
    const marker = markers[i];
    const end = i + 1 < markers.length ? markers[i + 1].start : raw.length;
    const occurrence = counts.get(marker.slug) ?? 0;
    counts.set(marker.slug, occurrence + 1);
    sections.push({
      slug: marker.slug,
      occurrence,
      text: raw.slice(marker.bodyStart, end),
      start: marker.start,
      end,
    });
  }
  return sections;
}

export function serializeSections(sections: Array<{ slug: string | null; text: string }>): string {
  let out = '';
  for (const section of sections) {
    if (section.slug !== null) {
      if (out !== '' && !out.endsWith('\n')) out += '\n';
      out += `-- ${section.slug}\n`;
    }
    out += section.text;
  }
  return out;
}

function findSection(
  sections: PromptSection[],
  slug: string,
  occurrence: number
): PromptSection | undefined {
  return sections.find((s) => s.slug === slug && s.occurrence === occurrence);
}

export function replaceSectionText(
  raw: string,
  slug: string,
  occurrence: number,
  newText: string
): string {
  const target = findSection(parseSections(raw), slug, occurrence);
  if (!target) return appendSection(raw, slug, newText, [slug]);
  const bodyStart = target.end - target.text.length;
  let body = newText;
  // A following block starts at a line boundary — keep it there.
  if (target.end < raw.length && body !== '' && !body.endsWith('\n')) body += '\n';
  return raw.slice(0, bodyStart) + body + raw.slice(target.end);
}

export function appendSection(
  raw: string,
  slug: string,
  text: string,
  orderedSlugs: string[]
): string {
  const sections = parseSections(raw);
  const idx = orderedSlugs.indexOf(slug);
  const newRank = idx === -1 ? Infinity : idx;
  // Insert after the last section whose slug sorts at or before the new one; the free
  // block (and slugs missing from orderedSlugs) rank first, so free text stays on top.
  const rankOf = (s: string | null): number => (s === null ? -1 : orderedSlugs.indexOf(s));
  let insertAt = 0;
  for (let i = 0; i < sections.length; i++) {
    if (rankOf(sections[i].slug) <= newRank) insertAt = i + 1;
  }
  const offset = insertAt === sections.length ? raw.length : sections[insertAt].start;
  let block = `-- ${slug}\n${text}`;
  if (offset < raw.length && !block.endsWith('\n')) block += '\n';
  const before = raw.slice(0, offset);
  const glue = before !== '' && !before.endsWith('\n') ? '\n' : '';
  return before + glue + block + raw.slice(offset);
}

/**
 * Append one empty section per slug as a single adjacent block. The block lands after the
 * last existing section whose slug is in `slugs`; with none present it takes the position
 * appendSection would give the first slug. Existing text is preserved verbatim.
 */
export function appendBlock(raw: string, slugs: string[], orderedSlugs: string[]): string {
  if (slugs.length === 0) return raw;
  const sections = parseSections(raw);
  const slugSet = new Set(slugs);
  let offset = -1;
  for (const section of sections) {
    if (section.slug !== null && slugSet.has(section.slug)) offset = section.end;
  }
  if (offset === -1) {
    // Mirror appendSection's rank placement for the block's first slug
    const idx = orderedSlugs.indexOf(slugs[0]);
    const newRank = idx === -1 ? Infinity : idx;
    const rankOf = (s: string | null): number => (s === null ? -1 : orderedSlugs.indexOf(s));
    let insertAt = 0;
    for (let i = 0; i < sections.length; i++) {
      if (rankOf(sections[i].slug) <= newRank) insertAt = i + 1;
    }
    offset = insertAt === sections.length ? raw.length : sections[insertAt].start;
  }
  const block = slugs.map((s) => `-- ${s}\n`).join('');
  const before = raw.slice(0, offset);
  const glue = before !== '' && !before.endsWith('\n') ? '\n' : '';
  return before + glue + block + raw.slice(offset);
}

export function removeSection(raw: string, slug: string, occurrence: number): string {
  const target = findSection(parseSections(raw), slug, occurrence);
  if (!target) return raw;
  return raw.slice(0, target.start) + raw.slice(target.end);
}

export function getSectionText(raw: string, slug: string, occurrence: number = 0): string | null {
  const target = findSection(parseSections(raw), slug, occurrence);
  return target ? target.text : null;
}

/** Drop comment lines (trimmed form starts with `--`) — mirrors backend filter_prompt_comments. */
export function stripComments(raw: string): string {
  if (!raw) return raw;
  return raw
    .split('\n')
    .filter((line) => !line.trimStart().startsWith('--'))
    .join('\n');
}
